"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import { IconBuildingStore } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import { TruncatedText } from "@/components/common/truncated-text";
import type { SellerAddonsGroup } from "@/features/addons/lib/group-addons-by-seller";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import { SummaryAddonsList } from "./summary-addons-list";

type SummaryAddonsGroupsProps = {
  groups: SellerAddonsGroup[];
  // Seller shown on rows whose addon names none (MPL-3832); only used while the
  // list stays flat — a grouped list names the seller in the group header.
  cateringName?: string;
  // Paddings of the group header and of each group's row list (the headers
  // bleed to the edges, so the collapsible row hosting the groups must drop its
  // own padding). The cart list widens both on desktop, the sidebar keeps the
  // 16px defaults — as the Design System's summaryAddonsRow does.
  headerClassName?: string;
  listClassName?: string;
  onEdit?: (addon: AddonDietAddonsRead) => void;
  onRemove?: (addonId: string) => void;
  renderRemoved?: (addonId: string) => ReactNode | null;
  children?: ReactNode;
};

// Addons of a position grouped by seller, as the Design System's
// summaryAddonsRow draws it (MPL-3833 / MPL-3834): a full-width grey header —
// store icon, "od {seller}", the seller's share of the amount — followed by
// that seller's rows, which no longer repeat the seller name. A selection from
// a single seller keeps the flat list.
export const SummaryAddonsGroups = ({
  groups,
  cateringName,
  headerClassName = "px-4 py-3",
  listClassName = "p-4",
  onEdit,
  onRemove,
  renderRemoved,
  children,
}: SummaryAddonsGroupsProps) => {
  const t = useTranslations();

  if (groups.length <= 1) {
    return (
      <div className={listClassName}>
        <SummaryAddonsList
          cateringName={cateringName}
          items={groups[0]?.items ?? []}
          onEdit={onEdit}
          onRemove={onRemove}
          renderRemoved={renderRemoved}
        >
          {children}
        </SummaryAddonsList>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {groups.map((group, index) => (
        <section key={group.key}>
          <header
            className={cn(
              "flex items-center justify-between gap-3 bg-brand-grey1-300",
              headerClassName,
            )}
          >
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-white">
                <IconBuildingStore
                  className="size-5 text-brand-green1-300"
                  stroke={1.5}
                />
              </span>
              {/* Seller names get cut off in the sidebar; the full one shows up
                  on hover once the pointer rests on the header for a moment. */}
              <h4 className="min-w-0 font-medium text-base text-brand-green1-300 leading-6">
                <TruncatedText
                  text={
                    group.name
                      ? t("cart.addons.seller.from", { name: group.name })
                      : t("cart.addons.seller.unknown")
                  }
                />
              </h4>
            </span>
            <span className="shrink-0 font-bold text-base text-brand-green1-300 leading-6">
              {getLocalePrice({ price: group.productTotal })}
            </span>
          </header>
          <div className={cn("flex flex-col", listClassName)}>
            <SummaryAddonsList
              hideSeller
              items={group.items}
              onEdit={onEdit}
              onRemove={onRemove}
              renderRemoved={renderRemoved}
            >
              {/* The design keeps the "add another" button inside the last
                  group's list, right under its rows. */}
              {index === groups.length - 1 ? children : null}
            </SummaryAddonsList>
          </div>
        </section>
      ))}
    </div>
  );
};
